// TanStack Query hooks for agent_files CRUD.
// All atomic writes go through Postgres RPCs (SECURITY DEFINER) per plan §3.1.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { AgentFile, AgentFileKind, AgentFileNode, Frontmatter } from "@/types/agent-skills";
import { StaleVersionError } from "@/types/agent-skills";

// ── Query keys (plan §1.3) ────────────────────────────────────────────────────

export const AGENT_FILES_KEY = ["agent-files"] as const;

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Build a nested tree from a flat list of non-deleted AgentFile rows.
 *  Siblings are sorted by `position` (ascending), then by `name` as a tiebreaker. */
export function buildTree(rows: AgentFile[]): AgentFileNode[] {
  const nodeMap = new Map<string, AgentFileNode>();

  for (const row of rows) {
    nodeMap.set(row.id, { ...row, children: [] });
  }

  const roots: AgentFileNode[] = [];

  for (const node of nodeMap.values()) {
    if (node.parent_id === null) {
      roots.push(node);
    } else {
      const parent = nodeMap.get(node.parent_id);
      if (parent) {
        parent.children.push(node);
      }
    }
  }

  function sortSiblings(nodes: AgentFileNode[]): AgentFileNode[] {
    return nodes
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.name.localeCompare(b.name))
      .map((n) => ({ ...n, children: sortSiblings(n.children) }));
  }

  return sortSiblings(roots);
}

/** Map a Supabase RPC error to a typed error when applicable. */
function mapRpcError(error: { message?: string; code?: string } | null): Error {
  if (!error) return new Error("Unknown error");
  if (error.message?.includes("stale_version") || error.code === "P0001") {
    return new StaleVersionError();
  }
  return new Error(error.message ?? "Unknown error");
}

// ── Query ─────────────────────────────────────────────────────────────────────

export function useAgentFiles() {
  const query = useQuery({
    queryKey: AGENT_FILES_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agent_files")
        .select("*")
        .is("deleted_at", null)
        .order("position", { ascending: true })
        .order("name", { ascending: true });

      if (error) throw new Error(error.message);

      // Cast JSONB frontmatter — the DB stores it as Json but we know the shape.
      return (data as AgentFile[]) ?? [];
    },
  });

  const tree = query.data ? buildTree(query.data) : [];

  return { tree, rows: query.data ?? [], isLoading: query.isLoading, error: query.error };
}

// ── Mutations ─────────────────────────────────────────────────────────────────

interface CreateAgentFileArgs {
  parentId: string | null;
  kind: AgentFileKind;
  name: string;
}

export function useCreateAgentFile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ parentId, kind, name }: CreateAgentFileArgs) => {
      const frontmatter: Frontmatter = { name, description: "" };

      const { data, error } = await supabase
        .from("agent_files")
        .insert({ parent_id: parentId, kind, name, frontmatter })
        .select()
        .single();

      if (error) throw new Error(error.message);

      return data as AgentFile;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_FILES_KEY });
    },
    onError: (err) => {
      console.error("hook:useCreateAgentFile", err);
      toast({
        title: "Failed to create file",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });
}

interface RenameAgentFileArgs {
  id: string;
  name: string;
}

export function useRenameAgentFile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, name }: RenameAgentFileArgs) => {
      const { data, error } = await supabase
        .from("agent_files")
        .update({ name })
        .eq("id", id)
        .select()
        .single();

      if (error) throw new Error(error.message);

      return data as AgentFile;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_FILES_KEY });
    },
    onError: (err) => {
      console.error("hook:useRenameAgentFile", err);
      toast({
        title: "Failed to rename file",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });
}

export function useDuplicateAgentFile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc("duplicate_agent_file", { p_id: id });

      if (error) throw new Error(error.message);

      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_FILES_KEY });
    },
    onError: (err) => {
      console.error("hook:useDuplicateAgentFile", err);
      toast({
        title: "Failed to duplicate file",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });
}

export function useSoftDeleteAgentFile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("soft_delete_agent_file", { p_id: id });

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_FILES_KEY });
    },
    onError: (err) => {
      console.error("hook:useSoftDeleteAgentFile", err);
      toast({
        title: "Failed to delete file",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });
}

// ── Reorder mutation ──────────────────────────────────────────────────────────

interface ReorderAgentFilesArgs {
  /** null means root-level agents */
  parentId: string | null;
  /** Sibling ids in the desired display order (first = position 0) */
  orderedIds: string[];
}

export function useReorderAgentFiles() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ parentId, orderedIds }: ReorderAgentFilesArgs) => {
      const { error } = await supabase.rpc("reorder_agent_files", {
        p_parent_id: parentId,
        p_ordered_ids: orderedIds,
      });

      if (error) throw new Error(error.message);
    },
    onMutate: async ({ parentId, orderedIds }) => {
      // Cancel any in-flight refetches so they don't overwrite our optimistic update.
      await queryClient.cancelQueries({ queryKey: AGENT_FILES_KEY });

      // Snapshot the previous data for rollback on error.
      const previous = queryClient.getQueryData<AgentFile[]>(AGENT_FILES_KEY);

      // Apply optimistic reorder: assign new position values to the reordered siblings.
      if (previous) {
        const updatedRows = previous.map((row) => {
          const newPos = orderedIds.indexOf(row.id);
          if (newPos !== -1 && row.parent_id === parentId) {
            return { ...row, position: newPos };
          }
          return row;
        });
        queryClient.setQueryData<AgentFile[]>(AGENT_FILES_KEY, updatedRows);
      }

      return { previous };
    },
    onError: (err, _vars, context) => {
      // Roll back to the snapshot taken in onMutate.
      if (context?.previous) {
        queryClient.setQueryData<AgentFile[]>(AGENT_FILES_KEY, context.previous);
      }
      console.error("hook:useReorderAgentFiles", err);
      toast({
        title: "Failed to reorder",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
    onSettled: () => {
      // Sync with the server after the mutation (success or error).
      queryClient.invalidateQueries({ queryKey: AGENT_FILES_KEY });
    },
  });
}

// ── Move mutation (cross-parent drag) ────────────────────────────────────────

interface MoveAgentFileArgs {
  /** The node to move */
  id: string;
  /** Target parent id; null = root level (agents only) */
  newParentId: string | null;
  /** Desired position index within the new parent's children */
  position: number;
  // Cache-key info for optimistic update
  oldParentId: string | null;
}

export function useMoveAgentFile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, newParentId, position }: MoveAgentFileArgs) => {
      const { error } = await supabase.rpc("move_agent_file", {
        p_id: id,
        p_new_parent_id: newParentId,
        p_position: position,
      });

      if (error) throw new Error(error.message);
    },
    onMutate: async ({ id, newParentId, position, oldParentId }) => {
      await queryClient.cancelQueries({ queryKey: AGENT_FILES_KEY });

      const previous = queryClient.getQueryData<AgentFile[]>(AGENT_FILES_KEY);

      if (previous) {
        const updatedRows = previous.map((row) => {
          if (row.id === id) {
            return { ...row, parent_id: newParentId, position };
          }
          // Shift old siblings after the removed position down.
          const movingRow = previous.find((r) => r.id === id);
          if (row.parent_id === oldParentId && movingRow && row.position > movingRow.position) {
            return { ...row, position: row.position - 1 };
          }
          // Shift new siblings at or after insertion position up.
          if (row.parent_id === newParentId && row.id !== id && row.position >= position) {
            return { ...row, position: row.position + 1 };
          }
          return row;
        });
        queryClient.setQueryData<AgentFile[]>(AGENT_FILES_KEY, updatedRows);
      }

      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData<AgentFile[]>(AGENT_FILES_KEY, context.previous);
      }
      console.error("hook:useMoveAgentFile", err);
      toast({
        title: "Failed to move file",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_FILES_KEY });
    },
  });
}

interface SaveAgentFileArgs {
  id: string;
  expectedVersion: number;
  frontmatter: Frontmatter;
  body: string;
  note?: string;
}

export function useSaveAgentFile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, expectedVersion, frontmatter, body, note }: SaveAgentFileArgs) => {
      const { data, error } = await supabase.rpc("save_agent_file", {
        p_id: id,
        p_expected_version: expectedVersion,
        p_frontmatter: frontmatter as unknown as Record<string, unknown>,
        p_body: body,
        p_note: note ?? null,
      });

      if (error) throw mapRpcError(error);

      return data as AgentFile;
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: AGENT_FILES_KEY });
      // Refresh the version-history list for this file so a newly-created
      // version appears immediately in the History drawer.
      queryClient.invalidateQueries({ queryKey: ["agent-file-versions", id] });
    },
    onError: (err) => {
      console.error("hook:useSaveAgentFile", err);
      // StaleVersionError is handled by the caller (StaleVersionDialog); don't toast for it.
      if (!(err instanceof StaleVersionError)) {
        toast({
          title: "Failed to save file",
          description: (err as Error).message,
          variant: "destructive",
        });
      }
    },
  });
}
